import axios from 'axios';

export interface User {
  id: number;
  imie: string;
  nazwisko: string;
  login: string;
  email: string;
  aktywny: boolean;
  ostatnie_logowanie: string | null;
  telefon: string | null;
  mfa_enabled: boolean;
  role_names?: string[];
  region_names?: string[]; // Added for region-based access control
}

interface LoginResponse {
  user: User;
  token: string;
}

interface AuthMeResponse {
  user: User;
}

// Configure axios base URL
// Subdomain mode: same-origin '/api'; allow override via VITE_API_BASE_URL at build time
const API_BASE_URL = ((import.meta as any)?.env?.VITE_API_BASE_URL as string) || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Helper to check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    // Add 30 second buffer to avoid edge cases
    return Date.now() >= (exp * 1000) - 30000;
  } catch {
    return true; // If we can't parse, assume expired
  }
}

// Helper to clear auth cookie on all possible domains
function clearAuthCookie() {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = [host];
  if (parts.length > 2) domains.push(parts.slice(1).join('.'));
  if (parts.length > 3) domains.push(parts.slice(2).join('.'));
  for (const domain of domains) {
    document.cookie = `authToken=; domain=.${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

// Add request interceptor to include token from sessionStorage in Authorization header
api.interceptors.request.use(
  (config) => {
    // Get token from sessionStorage (isolated per tab/window)
    try {
      const token = sessionStorage.getItem('authToken');
      if (token) {
        // Check if token is expired before sending
        if (isTokenExpired(token)) {
          console.warn('[AuthService] Token expired in axios interceptor, clearing tokens and redirecting');
          sessionStorage.removeItem('authToken');
          clearAuthCookie();
          redirectToLogin();
          return Promise.reject(new Error('Token expired'));
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // sessionStorage not available (e.g., private browsing)
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
// Note: Don't auto-redirect here - let AuthContext handle auth flow
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Just log auth errors, don't redirect - AuthContext handles auth state
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      console.warn('[AuthService] Auth error:', error?.response?.status);
    }
    return Promise.reject(error);
  }
);

class AuthService {
  private lastLoginAttempt = 0;
  private readonly LOGIN_COOLDOWN = 2000; // 2 seconds between login attempts

  async login(login: string, haslo: string): Promise<{ user: User; token: string }> {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastLoginAttempt;
    
    if (timeSinceLastAttempt < this.LOGIN_COOLDOWN) {
      const waitTime = this.LOGIN_COOLDOWN - timeSinceLastAttempt;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastLoginAttempt = Date.now();
    
    const response = await api.post('/auth/login', { login, haslo });
    const { user, token } = response.data as LoginResponse;
    // Token is legacy; cookie is authoritative. Keep return shape to avoid breaking callers.
    return { user, token };
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get('/auth/me');
      return (response.data as AuthMeResponse).user;
    } catch (error: any) {
      console.error('Error getting current user:', error);
      // If token is invalid/expired (401/403), clear it from sessionStorage AND cookie
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        try {
          sessionStorage.removeItem('authToken');
          clearAuthCookie();
          console.log('[AuthService] Cleared expired/invalid token from sessionStorage and cookie');
        } catch (e) {
          // sessionStorage not available
        }
      }
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // No local token to clear; cookie is cleared server-side
    }
  }

  // Cookie-based session only
  isAuthenticated(): boolean {
    try { return document.cookie.split(';').map((c) => c.trim()).some((c) => c.startsWith('sid=')); } catch { return false; }
  }

  getToken(): string | null { return null; }

  // Role-based access control methods
  hasRole(user: User | null, roleName: string): boolean {
    return user?.role_names?.includes(roleName) || false;
  }

  isAdmin(user: User | null): boolean {
    return this.hasRole(user, 'Administrator');
  }

  // Region-based access control methods
  hasRegion(user: User | null, regionName: string): boolean {
    return user?.region_names?.includes(regionName) || false;
  }

  hasAnyRegion(user: User | null, regionNames: string[]): boolean {
    return user?.region_names?.some(region => regionNames.includes(region)) || false;
  }

  canAccessRegionData(user: User | null, dataRegion: string): boolean {
    if (this.isAdmin(user)) return true; // Admins can access all data
    return this.hasRegion(user, dataRegion);
  }

  getUserRegions(user: User | null): string[] {
    return user?.region_names || [];
  }

  // Admin helpers (reuse same axios instance/baseURL)
  async getAdminData(endpoint: string): Promise<any> {
    const res = await api.get(endpoint);
    return res.data;
  }

  async postAdminData(endpoint: string, data: any): Promise<any> {
    const res = await api.post(endpoint, data);
    return res.data;
  }

  async putAdminData(endpoint: string, data: any): Promise<any> {
    const res = await api.put(endpoint, data);
    return res.data;
  }

  async deleteAdminData(endpoint: string): Promise<any> {
    const res = await api.delete(endpoint);
    return res.data;
  }

  // Zamowienie splits
  async getZamowienieSplits(zamowienieId: number) {
    const res = await api.get(`/zamowienia/${zamowienieId}/splits`);
    return res.data;
  }

  async putZamowienieSplits(zamowienieId: number, payload: { splits: Array<{ wezel_id: number; zamowione: number; zrealizowane?: number }>; enforceSumEq?: boolean }) {
    const res = await api.put(`/zamowienia/${zamowienieId}/splits`, payload);
    return res.data;
  }
}

export const authService = new AuthService();

/**
 * Helper function to add Authorization header to fetch requests
 * This ensures all fetch() calls include the token, just like axios interceptor does
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Get token from sessionStorage (same as axios interceptor)
  let token: string | null = null;
  try {
    token = sessionStorage.getItem('authToken');
  } catch (e) {
    // sessionStorage not available (e.g., private browsing)
  }

  // Check if token is expired BEFORE making request
  if (token && isTokenExpired(token)) {
    console.warn('[authenticatedFetch] Token expired, clearing and redirecting');
    sessionStorage.removeItem('authToken');
    clearAuthCookie();
    redirectToLogin();
    throw new Error('Token expired');
  }

  // Merge headers
  const headers = new Headers(init?.headers);
  
  // Add Authorization header if token exists
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Merge with existing init options
  const newInit: RequestInit = {
    ...init,
    headers,
    credentials: init?.credentials || 'include', // Keep credentials: 'include' for CORS
  };

  const response = await fetch(input, newInit);
  
  // Handle 401/403 from backend - but ONLY clear tokens if we actually sent a token
  // This prevents race condition where early requests (before auth) clear the cookie
  if ((response.status === 401 || response.status === 403) && token) {
    console.warn('[authenticatedFetch] Got 401/403 WITH token, clearing tokens');
    try { sessionStorage.removeItem('authToken'); } catch {}
    clearAuthCookie();
    redirectToLogin();
  } else if (response.status === 401 || response.status === 403) {
    console.log('[authenticatedFetch] Got 401/403 but no token was sent - not clearing (auth not ready yet)');
  }

  return response;
}

/**
 * Redirect to portal login
 */
function redirectToLogin(): void {
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 3) {
    const tenantHost = parts.slice(1).join('.');
    window.location.assign(`https://${tenantHost}/`);
  } else {
    window.location.assign('/');
  }
} 